import React, {useState} from "react"
import {compact, get, isEmpty} from "lodash"
import {UserOutlined} from "@ant-design/icons"
import {Popover, Tooltip} from "antd"
import {getColorContrast, getFullName} from "helpers"
import {EventPreviewContent} from "./EventPreviewContent"
import styles from "./EventContent.module.css"
import moment from "moment-timezone"
import {useModel} from "hooks"
import {CalendarEventType} from "types/calendar"
import {isMonthView} from "../../calendarViews"

export function EventContent(props) {
    const [isShowEventPreview, setIsShowEventPreview] = useState(false)
    const model = useModel()
    const {
        updateViewTo,
        eventInfo,
        clickInfo,
        handleShowEventPreview,
        isShowGoing,
        eventOptions,
        isWorkScheduleView,
        linkedCalendars
    } = props
    const backgroundColor = isMonthView(updateViewTo)
        ? "#fff"
        : get(eventInfo, "event._def.ui.backgroundColor", "#1e90ff")

    const closeEventPreview = () => {
        setIsShowEventPreview(false)
        setTimeout(() => {
            handleShowEventPreview(false)
            // To trigger handleDateClick event happen before close
        }, 300)
        clickInfo?.el?.parentElement?.classList?.remove(styles.highlightEventPreviewWrap)
        clickInfo?.el?.classList?.remove(styles.highlightEventPreview)
    }

    const onEventPreviewChange = (visible) => {
        if (!visible) {
            clickInfo?.el?.parentElement?.classList?.remove(styles.highlightEventPreviewWrap)
            clickInfo?.el?.classList?.remove(styles.highlightEventPreview)
        } else {
            clickInfo?.el?.parentElement?.classList?.add(styles.highlightEventPreviewWrap)
            clickInfo?.el?.classList?.add(styles.highlightEventPreview)
        }
        setIsShowEventPreview(visible)
        setTimeout(() => {
            handleShowEventPreview(visible)
            // To trigger handleDateClick event happen before close
        }, 300)
    }

    const renderEventPreviewContent = (eventInfo) => {
        return (
            <EventPreviewContent
                eventInfo={eventInfo}
                isShowGoing={isShowGoing}
                eventOptions={eventOptions}
                linkedCalendars={linkedCalendars}
                closeEventPreview={closeEventPreview}
                isWorkScheduleView={isWorkScheduleView}
            />
        )
    }

    const renderMonthView = () => {
        return (
            <div className={styles.eventMonthView}>
                <div className={styles.dot} style={{background: eventInfo.event.backgroundColor}}></div>
                <div className={styles.eventTitleMonthView} style={{color: getColorContrast(backgroundColor).color}}>
                    <span>{eventInfo.event.title}</span>
                </div>
            </div>
        )
    }

    const renderInvitees = (invitedUsersLabel: string, color: string) => (
        <Tooltip title={invitedUsersLabel}>
            <div className={styles.eventInvitees} style={{color}}>
                <UserOutlined className={styles.eventInviteesIcon} />
                <span className={styles.eventInviteesText}>{invitedUsersLabel}</span>
            </div>
        </Tooltip>
    )

    const renderOtherView = () => {
        const format = model.getUserTimeFormat()
        const {schedule_suffix, moduleTitle, subModuleTitle, type, invitedUsers, allDay} = eventInfo.event.extendedProps
        const courseScheduleInfo = [schedule_suffix, moduleTitle, subModuleTitle].filter((item) => item).join(", ")
        const durationHours = moment(eventInfo.event.end).diff(eventInfo.event.start, "hour")
        const color = getColorContrast(backgroundColor).color
        const timePreview =
            type === CalendarEventType.EndTermDate
                ? moment(eventInfo.event.start).endOf("date")
                : moment(eventInfo.event.start)
        const title = eventInfo.event.title
        const isAllDay = allDay || eventInfo.event.allDay
        const timeLabel = isAllDay ? null : timePreview.format(format)
        const invitedUsersLabel = compact(invitedUsers)
            .map((user) => getFullName(user))
            .filter(Boolean)
            .join(", ")
        const hasInvitees = !isEmpty(invitedUsersLabel)
        const isLongEvent = durationHours >= 1
        const metaParts = compact([timeLabel, courseScheduleInfo])

        if (isLongEvent) {
            return (
                <div className={styles.eventContent}>
                    {title && (
                        <p className={styles.eventTitle} style={{color}} title={title}>
                            {title}
                        </p>
                    )}
                    {courseScheduleInfo && (
                        <p className={styles.eventMeta} style={{color}} title={courseScheduleInfo}>
                            {courseScheduleInfo}
                        </p>
                    )}
                    {timeLabel && (
                        <p className={styles.eventMeta} style={{color}}>
                            {timeLabel}
                        </p>
                    )}
                    {hasInvitees && renderInvitees(invitedUsersLabel, color)}
                </div>
            )
        }

        const compactTitle = [title, ...metaParts].filter(Boolean).join(" · ")

        return (
            <div className={styles.eventContent}>
                {compactTitle && (
                    <p className={styles.eventTitle} style={{color}} title={compactTitle}>
                        {title && <span>{title}</span>}
                        {metaParts.length > 0 && (
                            <>
                                {title && <span className={styles.eventMetaSeparator}> · </span>}
                                <span className={styles.eventMetaInline}>{metaParts.join(" · ")}</span>
                            </>
                        )}
                    </p>
                )}
                {hasInvitees && renderInvitees(invitedUsersLabel, color)}
            </div>
        )
    }

    return (
        <Popover
            overlayClassName="event-content-preview"
            visible={isShowEventPreview}
            content={() => renderEventPreviewContent(eventInfo.event)}
            trigger="click"
            onVisibleChange={onEventPreviewChange}
            placement="left">
            {isMonthView(updateViewTo) ? renderMonthView() : renderOtherView()}
        </Popover>
    )
}
